<template>
  <div v-if="loggedIn" class="nb-wrap" :class="{floating: floating}" :style="floating ? floatingStyle : null">
    <div class="nb-btn" @click="$router.push('/notifications')" :title="bellTooltip">
      <i class="mdi mdi-bell-outline nb-icon" :class="{'nb-active': hasRecentActivity}"></i>
      <span v-if="unreadCount > 0" class="nb-badge">{{ displayCount }}</span>
      <span v-else-if="hasRecentActivity" class="nb-pulse"></span>
    </div>
  </div>
</template>

<script>
import { executeJson, flocker } from "@/api";
export default {
  name: "NotificationBell",
  props: {
    // When true (default), the bell positions itself floating in the top-right corner —
    // suitable for pages that don't already have a topbar bell. Set `floating="false"`
    // to drop the bell inline inside an existing topbar.
    floating: { type: Boolean, default: true },
  },
  data(){return{
    notifications: [],
    unreadCount: 0,
    pollTimer: null,
    onFocusHandler: null,
    onNotifChanged: null,
    loggedInTick: !!sessionStorage.getItem('userId'),
  }},
  watch:{
    // sessionStorage isn't reactive — re-check on every route change so the bell
    // hides immediately after logout (and shows after login).
    '$route'(){ this.loggedInTick = !!sessionStorage.getItem('userId'); if(this.loggedInTick) this.fetch(); }
  },
  computed:{
    loggedIn(){ return this.loggedInTick; },
    floatingStyle(){
      // Push down a bit so it doesn't collide with native topbars on landing/marketplace pages
      return 'position:fixed;top:14px;right:18px;z-index:2500;';
    },
    recentActivityCount(){
      if(!this.notifications || !this.notifications.length) return 0;
      var cutoff = Date.now() - 24*60*60*1000;
      return this.notifications.filter(function(n){
        if(!n.created_at) return false;
        return new Date(n.created_at).getTime() >= cutoff;
      }).length;
    },
    hasRecentActivity(){ return this.recentActivityCount > 0; },
    displayCount(){ return this.unreadCount > 99 ? '99+' : this.unreadCount; },
    bellTooltip(){
      if(this.unreadCount > 0) return this.unreadCount + ' unread notification' + (this.unreadCount > 1 ? 's' : '');
      if(this.hasRecentActivity) return this.recentActivityCount + ' recent activity in the last 24h';
      return 'Notifications';
    }
  },
  mounted(){
    if(!this.loggedIn) return;
    var self = this;
    this.fetch();
    // Poll every 60s while the tab is visible
    this.pollTimer = setInterval(function(){
      if(document.visibilityState !== 'hidden') self.fetch();
    }, 60000);
    // Refresh on focus + when other parts of the app mark notifications read
    this.onFocusHandler = function(){ self.fetch(); };
    window.addEventListener('focus', this.onFocusHandler);
    this.onNotifChanged = function(){ self.fetch(); };
    window.addEventListener('flockr-notifications-changed', this.onNotifChanged);
  },
  beforeUnmount(){
    if(this.pollTimer) clearInterval(this.pollTimer);
    if(this.onFocusHandler) window.removeEventListener('focus', this.onFocusHandler);
    if(this.onNotifChanged) window.removeEventListener('flockr-notifications-changed', this.onNotifChanged);
  },
  methods:{
    async fetch(){
      var uid = sessionStorage.getItem('userId');
      if(!uid) return;
      try{
        var r = await executeJson({function:"getNotifications", userId:uid, limit:10}, flocker);
        if(r.data && r.data.success){
          this.notifications = (r.data.data && r.data.data.notifications) || [];
          this.unreadCount = parseInt(r.data.data.unread || 0, 10) || 0;
        }
      }catch(e){ /* silent — bell is best-effort */ }
    }
  }
};
</script>

<style scoped>
.nb-wrap{display:inline-block}
.nb-wrap.floating .nb-btn{box-shadow:0 4px 14px rgba(0,0,0,.25)}
.nb-btn{position:relative;cursor:pointer;width:42px;height:42px;display:flex;align-items:center;justify-content:center;border-radius:50%;background:rgba(20,20,20,.85);border:1px solid rgba(191,161,100,.35);backdrop-filter:blur(8px);transition:all .2s}
.nb-btn:hover{background:rgba(20,20,20,1);border-color:#BFA164;transform:translateY(-1px)}
.nb-icon{color:#BFA164;font-size:22px;line-height:1;transition:color .2s}
.nb-icon.nb-active{color:#FFD27A;animation:nbShake 4s ease-in-out infinite}
.nb-badge{position:absolute;top:-2px;right:-2px;background:#FF6B6B;color:#fff;font-size:10px;font-weight:700;border-radius:10px;padding:1px 5px;min-width:18px;text-align:center;line-height:1.4;box-shadow:0 0 0 2px #0A0A0A}
.nb-pulse{position:absolute;top:7px;right:8px;width:9px;height:9px;border-radius:50%;background:#BFA164;box-shadow:0 0 0 2px rgba(20,20,20,.85),0 0 0 0 rgba(191,161,100,.7);animation:nbPulse 2.2s ease-in-out infinite}

/* Light-theme tints */
html[data-theme="light"] .nb-btn{background:rgba(255,255,255,.95);border-color:rgba(191,161,100,.5);box-shadow:0 4px 14px rgba(0,0,0,.08)}
html[data-theme="light"] .nb-btn:hover{background:#FFFFFF;border-color:#BFA164}
html[data-theme="light"] .nb-icon{color:#8A6F3D}
html[data-theme="light"] .nb-badge{box-shadow:0 0 0 2px #FFFFFF}
html[data-theme="light"] .nb-pulse{box-shadow:0 0 0 2px #FFFFFF,0 0 0 0 rgba(191,161,100,.7)}

@keyframes nbPulse{
  0%, 100%{ box-shadow:0 0 0 2px rgba(20,20,20,.85), 0 0 0 0 rgba(191,161,100,.65); }
  50%{ box-shadow:0 0 0 2px rgba(20,20,20,.85), 0 0 0 6px rgba(191,161,100,0); }
}
@keyframes nbShake{
  0%, 88%, 100% { transform: rotate(0); }
  90% { transform: rotate(10deg); }
  92% { transform: rotate(-10deg); }
  94% { transform: rotate(6deg); }
  96% { transform: rotate(-4deg); }
  98% { transform: rotate(2deg); }
}

/* On phones the floating bell collides with every page's own topbar action buttons
   (Cancel / + New Listing / Save). Users still reach notifications via the Dashboard's
   inline bell + the bottom tab bar's Home tab, so we hide the floating instance on mobile. */
@media (max-width:768px){
  .nb-wrap.floating{display:none}
}
</style>
